import { forgotPasswordSchema, type ForgotPasswordInput } from '@church/domain'
import { submitSafely, useForgotPassword } from '@church/data'
import { faArrowLeft, faEnvelopeOpenText, faPaperPlane } from '@church/ui-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { Button, ErrorNote, Field, Icon, Input } from '../components/ui.tsx'

export const Route = createFileRoute('/_auth/forgot-password')({
  component: ForgotPasswordScreen,
})

function ForgotPasswordScreen() {
  const forgot = useForgotPassword()
  const form = useForm<ForgotPasswordInput>({ resolver: zodResolver(forgotPasswordSchema) })

  const submit = form.handleSubmit(async ({ email }) => {
    await forgot.mutateAsync({ email, redirectTo: `${window.location.origin}/reset-password` })
  })

  if (forgot.isSuccess) {
    return (
      <div className="space-y-3 text-center">
        <Icon icon={faEnvelopeOpenText} className="mx-auto size-10 text-content-brand" />
        <h1 className="text-xl font-semibold text-content-default">Check your inbox</h1>
        <p className="text-sm text-content-muted">
          If that address has an account, a reset link is on its way.
        </p>
        <Link
          to="/sign-in"
          className="inline-flex items-center gap-1.5 text-sm text-content-brand hover:underline"
        >
          <Icon icon={faArrowLeft} className="size-3.5" />
          Back to sign-in
        </Link>
      </div>
    )
  }

  return (
    <form onSubmit={submitSafely(submit)} className="space-y-4" noValidate>
      <h1 className="text-xl font-semibold text-content-default">Reset your password</h1>
      <p className="text-sm text-content-muted">
        Enter your email and we&rsquo;ll send you a link to set a new one.
      </p>

      <Field label="Email" error={form.formState.errors.email?.message}>
        <Input type="email" autoComplete="email" {...form.register('email')} />
      </Field>

      <ErrorNote error={forgot.error} />

      <Button type="submit" icon={faPaperPlane} className="w-full" disabled={forgot.isPending}>
        {forgot.isPending ? 'Sending…' : 'Send reset link'}
      </Button>

      <p className="text-center text-sm">
        <Link
          to="/sign-in"
          className="inline-flex items-center gap-1.5 text-content-brand hover:underline"
        >
          <Icon icon={faArrowLeft} className="size-3.5" />
          Back to sign-in
        </Link>
      </p>
    </form>
  )
}
